import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, ChevronLeft, ChevronRight, Eye, RefreshCw, Shield,
} from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(valor) {
  if (valor == null) return '—';
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtData(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

const FORMA_LABEL = { avista: 'À vista', cartao: 'Cartão', crediario: 'Crediário' };

const RESULTADO_BADGE = {
  viavel: { texto: 'Viável', cls: 'bg-green-100 text-green-800' },
  gerente: { texto: 'Liberação gerente', cls: 'bg-red-100 text-red-700' },
  inviavel: { texto: 'Inviável', cls: 'bg-red-600 text-white' },
};

// ─── Componente principal ─────────────────────────────────────────────────────

export default function HistoricoSimulacoesViabilidade() {
  const navigate = useNavigate();
  const { isGerenteOuAdmin } = useAuth();

  // Filtros
  const [filtroProduto, setFiltroProduto] = useState('');
  const [filtroOperador, setFiltroOperador] = useState('');

  // Dados
  const [simulacoes, setSimulacoes] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  // ── Buscar ─────────────────────────────────────────────────────────────────
  const buscar = useCallback(async (page = 1) => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.simulacaoViabilidade.listarTodas({
        page,
        limit: 20,
        nomeProduto: filtroProduto.trim() || undefined,
        usuario:     filtroOperador.trim() || undefined,
      });
      const d = resp.data;
      setSimulacoes(d.data ?? []);
      setPagination(d.pagination ?? { total: 0, page: 1, limit: 20, pages: 1 });
    } catch {
      setErro('Erro ao carregar simulações. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, [filtroProduto, filtroOperador]);

  // Carrega ao montar
  useEffect(() => { buscar(1); }, []); // eslint-disable-line

  function handleBuscar(e) {
    e.preventDefault();
    buscar(1);
  }

  function irPagina(p) {
    if (p < 1 || p > pagination.pages) return;
    buscar(p);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!isGerenteOuAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Acesso Negado</h2>
          <p className="text-gray-600 mt-2">Apenas gerentes ou administradores podem acessar esta página</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Histórico de Simulações</h1>
          <p className="text-sm text-gray-500">Consulta de todas as simulações de viabilidade de desconto realizadas</p>
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={handleBuscar} className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-gray-500 block mb-1">Nome do produto</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Buscar por produto..."
              value={filtroProduto}
              onChange={e => setFiltroProduto(e.target.value)}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">Responsável</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Nome do operador..."
              value={filtroOperador}
              onChange={e => setFiltroOperador(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFiltroProduto('');
                setFiltroOperador('');
                setTimeout(() => buscar(1), 0);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-1.5"
            >
              <RefreshCw size={13} />Limpar
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
            >
              <Search size={14} />Buscar
            </button>
          </div>
        </div>
      </form>

      {/* Tabela */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

        {/* Totalizador */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-500">
            {loading ? 'Carregando...' : `${pagination.total} simulação(ões) encontrada(s)`}
          </span>
          {pagination.pages > 1 && (
            <span className="text-xs text-gray-400">
              Página {pagination.page} de {pagination.pages}
            </span>
          )}
        </div>

        {erro && (
          <div className="px-5 py-8 text-center text-sm text-red-600">{erro}</div>
        )}

        {!erro && !loading && simulacoes.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            Nenhuma simulação encontrada com os filtros informados.
          </div>
        )}

        {!erro && simulacoes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Produto</th>
                  <th className="text-center px-4 py-3 font-medium">Condição</th>
                  <th className="text-right px-4 py-3 font-medium">Preço solicitado</th>
                  <th className="text-center px-4 py-3 font-medium">Resultado</th>
                  <th className="text-left px-4 py-3 font-medium">Responsável</th>
                  <th className="text-center px-4 py-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {simulacoes.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50 transition-colors">

                    {/* Data */}
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-gray-900 font-medium">{fmtData(s.created_at)}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(s.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>

                    {/* Produto */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[220px]">{s.produto_nome || '—'}</div>
                      <div className="text-xs text-gray-400">{s.categoria === 'moveis' ? 'Móveis' : 'Eletrodomésticos'}</div>
                    </td>

                    {/* Parcelas */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-gray-700">{s.num_parcelas === 0 ? 'À vista' : `${s.num_parcelas}x`}</span>
                      {s.forma_pagamento && s.forma_pagamento !== 'avista' && (
                        <div className="text-xs text-gray-400">{FORMA_LABEL[s.forma_pagamento]}</div>
                      )}
                    </td>

                    {/* Preço solicitado */}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span className="font-medium text-gray-900">R$ {fmt(s.preco_solicitado)}</span>
                    </td>

                    {/* Resultado */}
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      {RESULTADO_BADGE[s.resultado] ? (
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${RESULTADO_BADGE[s.resultado].cls}`}>
                          {RESULTADO_BADGE[s.resultado].texto}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>

                    {/* Responsável */}
                    <td className="px-4 py-3">
                      <span className="text-gray-700 text-sm">{s.usuario || '—'}</span>
                    </td>

                    {/* Ações */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => navigate(`/simulador-viabilidade?id=${s.id}`)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-blue-300 bg-blue-50 rounded-lg text-xs text-blue-700 hover:bg-blue-100 transition-colors"
                      >
                        <Eye size={13} />Visualizar
                      </button>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button
              onClick={() => irPagina(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />Anterior
            </button>

            {/* Páginas */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                let p;
                if (pagination.pages <= 7) {
                  p = i + 1;
                } else if (pagination.page <= 4) {
                  p = i + 1;
                } else if (pagination.page >= pagination.pages - 3) {
                  p = pagination.pages - 6 + i;
                } else {
                  p = pagination.page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => irPagina(p)}
                    disabled={loading}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                      ${p === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => irPagina(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próxima<ChevronRight size={14} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
